import s from './MineExplore.module.sass';

import React, { FC, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Header,
  HorizontalScroll,
  InlineHeader,
  LottieIcon,
  Placeholder,
  ScrollView,
} from '$uikit';

import { Swiper, SwiperSlide } from 'swiper/react';
import { SwiperRef } from 'swiper/react/swiper-react';
import { classNames, shuffle, statReachGoal } from '$utils';
import { useDispatch, useSelector } from 'react-redux';
import { mineActions, selectMine } from '$store/mine';
import { Loader } from '$shared/components';
import { useTranslator } from '$hooks';
import { MineExploreCard, MineExploreCardContentTypeEnum } from '$store/models';
import { ExploreCard } from '$core/MineExplore/ExploreCard/ExploreCard';
import { AuthPopoverSimple } from '$core/AuthPopover/AuthPopover';
import { goTo } from '$navigation/helper';
import { MINE } from '$shared/constants/pages';

export const MineExplore: FC = () => {
  const t = useTranslator();
  const dispatch = useDispatch();
  const slider = useRef<SwiperRef>(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const { exploreLoadingStatus: loadingStatus, explore } = useSelector(selectMine);

  const loadData = () => {
    dispatch(mineActions.loadExplore());
  };

  useLayoutEffect(() => {
    loadData();
    statReachGoal('mine_explore_visit');
  }, []);

  const levels = useMemo(() => {
    const result: { canBuy: boolean; cards: MineExploreCard[]; boughtCount: number }[] =
      [];
    let prevOpened = true;
    for (const c of explore) {
      const prev = result[result.length - 1];
      if (!prev || prev.cards[0].level !== c.level) {
        if (prev) {
          prevOpened = prev.boughtCount >= 5;
          const cards = shuffle([...prev.cards]);
          prev.cards = cards
            .filter((card) => card.isBought)
            .concat(cards.filter((card) => !card.isBought));
        }

        result.push({
          canBuy: prevOpened,
          cards: [],
          boughtCount: 0,
        });
      }

      const item = result[result.length - 1];
      if (c.isBought) {
        item.boughtCount++;
      }
      item.cards.push(c);
    }

    return result;
  }, [explore]);

  function renderContent() {
    if (loadingStatus === 'failed') {
      return (
        <Placeholder
          title={t('failed_title')}
          actions={<Button onClick={loadData}>{t('failed_button')}</Button>}
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    if (loadingStatus === 'loading' && explore.length === 0) {
      return (
        <Placeholder>
          <Loader />
        </Placeholder>
      );
    }

    return (
      <>
        {levels.map((level) => {
          const levelNo = level.cards[0].level;
          const cards = level.cards.filter((_, i) => i % 2 === 0);
          const hasVideo =
            cards.filter(
              (item) => item.contentType === MineExploreCardContentTypeEnum.video,
            ).length > 0;

          return (
            <div key={levelNo}>
              <InlineHeader>
                {t('mine_explore_level_title', {
                  level: levelNo,
                })}
                {hasVideo && (
                  <div className={s.cardsVideoBadge}>{t('mine_explore_video_badge')}</div>
                )}
              </InlineHeader>
              {!level.canBuy && (
                <div className={s.levelBlocked}>{t('mine_explore_level_locked')}</div>
              )}
              <HorizontalScroll
                className={classNames({
                  [s.cardsWrap]: true,
                  [`_mine_explore_cards_${levelNo}`]: true,
                })}
              >
                <div className={s.cards}>
                  {cards.map((card) => (
                    <ExploreCard key={card.id} card={card} canBuy={level.canBuy} />
                  ))}
                </div>
                <div className={s.cards}>
                  {level.cards
                    .filter((_, i) => i % 2 !== 0)
                    .map((card) => (
                      <ExploreCard key={card.id} card={card} canBuy={level.canBuy} />
                    ))}
                </div>
              </HorizontalScroll>
            </div>
          );
        })}
      </>
    );
  }

  const slides = [
    {
      icon: 'logo',
      title: t('mine_explore_slide1_title'),
      description: t('mine_explore_slide1_text'),
    },
    {
      icon: 'duck_out',
      title: t('mine_explore_slide2_title'),
      description: t('mine_explore_slide2_text'),
    },
    {
      icon: 'duck_money',
      title: t('mine_explore_slide3_title'),
      description: t('mine_explore_slide3_text'),
    },
  ];

  return (
    <>
      <Header showBackButton isTransparent />
      <ScrollView>
        <div style={{ height: 42 }} />
        <Swiper
          ref={slider}
          spaceBetween={16}
          slidesPerView={1}
          autoplay={{
            delay: 10000,
          }}
          loop
          onSlideChange={(swiper) => setActiveSlide(swiper.realIndex)}
        >
          {slides.map((slide, index) => (
            <SwiperSlide key={index}>
              <div className={s.slide}>
                <div>
                  <LottieIcon name={slide.icon} size={84} loop />
                </div>
                <div className={s.slideTitle}>{slide.title}</div>
                <div className={s.slideDescription}>{slide.description}</div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
        <div className={s.sliderBullets}>
          {slides.map((_, index) => (
            <div
              className={classNames({
                [s.sliderBullet]: true,
                [s.sliderBulletActive]: index === activeSlide,
              })}
              key={index}
              onClick={() => slider.current?.swiper.slideTo(index)}
            />
          ))}
        </div>
        <div className={s.mineButton}>
          <Button onClick={() => goTo(MINE)} size="medium">
            {t('mine_explore_mine_button')}
          </Button>
        </div>
        {renderContent()}
      </ScrollView>
      <AuthPopoverSimple />
    </>
  );
};
